/**
 * Created by piotr.pozniak@thebeaverhead.com on 17/03/2023.
 */

import React, { useCallback, useState } from "react";

import { Link } from "react-router-dom";
import SidebarTab from "../../../component/WidgetSettings/SidebarTab";
import SuccessAlert from "../../../component/SuccessAlert";
import ApiError from "../../../component/ApiError";
import LoadingIndicator from "../../../../calendar/components/LoadingIndicator";
import { useCurrentOrganizationStore } from "../../../../../hooks/redux/currentOrganization";
import Accordion from "../../../component/WidgetSettings/Acordion";
import RadioInput from "../../../component/WidgetSettings/RadioInput";
import WidgetInstallModal from "../../../modals/WidgetInstallModal";
import classNames from "classnames";
import {
  canPublishWidgetWithinQuota,
  getTheLowestPlanMeetingCalendarCriteria,
  hasRemainingWidgetQuota,
} from "../../../tools";

/**
 *
 * @param props
 * @param {Object} props.widgetModel - The widget model object.
 * @param {Object} props.widget - The widget object containing model and update status.
 * @param {Object} props.errors - The errors object containing form errors.
 * @param {Function} props.onUpdateStatus - Function to update the widget status.
 * @param {boolean} props.disabled - Whether the tab is disabled.
 * @param {string} props.widgetTypeName - The name of the widget type (e.g., "Calendar").
 * @param {string} props.widgetType - The type of the widget (e.g., "calendar").
 * @param {string} props.shareableUrl - The shareable URL for the widget.
 * @param {Function} props.onSubmit - Function to submit the widget settings.
 * @param {Function} props.onUpdateField - Function to update a field in the form.
 * @param {boolean} props.isNew - Whether the widget is new.
 * @param {boolean} props.hasDomains - Whether the widget has domains.
 *
 * @returns {Element}
 * @constructor
 */
const InstallTab = (props) => {
  const [showInstallModal, setShowInstallModal] = useState(false);

  const { widgetModel, widget } = props;
  const widgetType = widget.model.widget_type;
  const { currentOrganization } = useCurrentOrganizationStore();

  const canPublish = canPublishWidgetWithinQuota(
    currentOrganization,
    Boolean(widget.model.available)
  );

  const recommendedPlan = !canPublish
    ? getTheLowestPlanMeetingCalendarCriteria(currentOrganization)
    : null;

  const publishRestrictionWarning =
    !widgetModel.available && !canPublish ? (
      <div className={"alert alert-danger mt-3 p-2"}>
        <i className={"text-danger m-r-5 material-icons icon-sm"}>warning</i>
        <p>
          You cannot publish this {widgetType} widget because you have reached
          the maximum number of widgets allowed for your current plan.
        </p>
        {recommendedPlan && (
          <p>
            Click here to upgrade to the minimum required plan:{" "}
            <strong>{recommendedPlan}</strong>.
          </p>
        )}
        <div className={"d-flex justify-content-center"}>
          <Link to={"/billing"}>
            <button className={"btn btn-success btn-sm"}>Upgrade now</button>
          </Link>
        </div>
      </div>
    ) : null;

  /**
   *
   * @type {(function(*): void)|*}
   */
  const onChangeStatus = useCallback(
    (field) => (e) => {
      props.onUpdateStatus(!(widget.model.available * 1) ? 1 : 0);
    },
    [props.onUpdateStatus, widget]
  );

  /**
   *
   * @type {(function(*): void)|*}
   */
  const onPublishClick = useCallback(
    (e) => {
      props.onUpdateStatus(1);
    },
    [props.onUpdateStatus, widget]
  );

  /**
   *
   * @type {(function(): void)|*}
   */
  const onShowInstallModal = useCallback(() => {
    setShowInstallModal(true);
  }, [setShowInstallModal]);

  /**
   *
   * @type {(function(): void)|*}
   */
  const onCloseInstallModal = useCallback(() => {
    setShowInstallModal(false);
  }, [setShowInstallModal]);

  const widgetTypeName = (props.widgetTypeName || "Widget").toLowerCase();

  const installModal = showInstallModal ? (
    <WidgetInstallModal
      widgetType={widgetType}
      uuid={widget.model.uuid}
      slug={widget.model.slug}
      show={showInstallModal}
      onClose={onCloseInstallModal}
      widgetTypePrintable={"event alerts"}
      widgetTemplate={widget.model.template}
    />
  ) : null;

  const unpublishedWarning =
    !widgetModel.available && canPublish ? (
      <div
        className={
          "alert alert-warning fade show d-flex align-items-center flex-column p-2"
        }
      >
        This widget is unpublished. Publish your widget first.
        <button className="btn btn-primary btn-sm m-2" onClick={onPublishClick}>
          Publish
        </button>
      </div>
    ) : null;

  const installButton =
    widgetModel.available && canPublish ? (
      <div className={"d-flex flex-column"}>
        <div className={"my-2"}>
          <button
            className={classNames("btn btn-primary w-100", {
              disabled: props.disabled || !widgetModel.available,
            })}
            disabled={props.disabled || !widgetModel.available}
            onClick={onShowInstallModal}
          >
            Install
          </button>
        </div>

        <div className={"mx-2"}>{unpublishedWarning}</div>
        {installModal}
      </div>
    ) : null;

  const publishCard = (
    <Accordion
      title={"Publish"}
      subtitle={`Your ${widgetTypeName} is ${
        widget.model.available * 1 === 0 ? "is not" : ""
      } published.`}
      keepExpanded={true}
    >
      <RadioInput
        fieldName={"available"}
        label={"Make this widget available to others"}
        onChange={onChangeStatus}
        checked={widgetModel.available}
        disabled={!widgetModel.available}
        tooltip={
          widget.model.available * 1 === 0
            ? {
                message: `In order to share your ${widgetTypeName} and make it accessible by
              others, change it status to published. You can make it here using
              the toggle button.`,
              }
            : null
        }
      />

      {publishRestrictionWarning}
      {installButton}
      <SuccessAlert className={"p-2"}>
        {widget.updateSuccess ? "Your changes have been saved." : null}
      </SuccessAlert>
    </Accordion>
  );

  const footer = !props.disabled ? (
    <div>
      <ApiError
        error={widget.updateError}
        defaultErrorMessage={"Could not save your settings. Please try again"}
      />
    </div>
  ) : (
    <LoadingIndicator />
  );

  return (
    <SidebarTab id="install_tab" show={false} footer={footer}>
      {publishCard}
    </SidebarTab>
  );
};

export default InstallTab;
